import { useMemo } from "react";
import { useAppSelector } from "./reduxHooks";
import { useApiAllUserApplicationsList } from "@/api/lctApIComponents";
import { CircleCheck, Clock4, TriangleAlert, Info } from "lucide-react";
import { MetricProps } from "@/dashboard/components/Metric";

export const useApplicationMetrics = (): MetricProps[] => {
  const token = useAppSelector((state) => state.user.authKey);

  const { data: totalData, isLoading: isLoadingTotal } = useApiAllUserApplicationsList(
    {
      headers: { Authorization: `Token ${token}` },
      queryParams: {},
    },
    { enabled: !!token },
  );

  const startedQuery = useApiAllUserApplicationsList(
    {
      headers: { Authorization: `Token ${token}` },
      queryParams: { status: "STARTED" },
    },
    { enabled: !!token },
  );
  const part1SubmittedQuery = useApiAllUserApplicationsList(
    {
      headers: { Authorization: `Token ${token}` },
      queryParams: { status: "PART_1_SUBMITTED" },
    },
    { enabled: !!token },
  );
  const part1ApprovedQuery = useApiAllUserApplicationsList(
    {
      headers: { Authorization: `Token ${token}` },
      queryParams: { status: "PART_1_APPROVED" },
    },
    { enabled: !!token },
  );
  const part2SubmittedQuery = useApiAllUserApplicationsList(
    {
      headers: { Authorization: `Token ${token}` },
      queryParams: { status: "PART_2_SUBMITTED" },
    },
    { enabled: !!token },
  );
  const rejectedQuery = useApiAllUserApplicationsList(
    {
      headers: { Authorization: `Token ${token}` },
      queryParams: { status: "REJECTED" },
    },
    { enabled: !!token },
  );
  const approvedQuery = useApiAllUserApplicationsList(
    {
      headers: { Authorization: `Token ${token}` },
      queryParams: { status: "APPROVED" },
    },
    { enabled: !!token },
  );
  const furtherInfoPart1Query = useApiAllUserApplicationsList(
    {
      headers: { Authorization: `Token ${token}` },
      queryParams: { status: "FURTHER_INFO_PART_1" },
    },
    { enabled: !!token },
  );
  const furtherInfoPart2Query = useApiAllUserApplicationsList(
    {
      headers: { Authorization: `Token ${token}` },
      queryParams: { status: "FURTHER_INFO_PART_2" },
    },
    { enabled: !!token },
  );

  const { data: startedData, isLoading: isLoadingStarted } = startedQuery;
  const { data: part1SubmittedData, isLoading: isLoadingPart1Submitted } = part1SubmittedQuery;
  const { data: part1ApprovedData, isLoading: isLoadingPart1Approved } = part1ApprovedQuery;
  const { data: part2SubmittedData, isLoading: isLoadingPart2Submitted } = part2SubmittedQuery;
  const { data: rejectedData, isLoading: isLoadingRejected } = rejectedQuery;
  const { data: approvedData, isLoading: isLoadingApproved } = approvedQuery;
  const { data: furtherInfoPart1Data, isLoading: isLoadingFurtherInfoPart1 } = furtherInfoPart1Query;
  const { data: furtherInfoPart2Data, isLoading: isLoadingFurtherInfoPart2 } = furtherInfoPart2Query;
  const metrics = useMemo(() => {
    const metrics: MetricProps[] = [
      {
        metricTitle: "Total Applications",
        statValue: totalData?.count ?? 0,
        isLoading: isLoadingTotal,
      },
      {
        metricTitle: "Approved",
        statValue: approvedData?.count ?? 0,
        icon: CircleCheck,
        iconColor: "text-green-900",
        iconBgColor: "bg-green-100",
        isLoading: isLoadingApproved,
      },
      {
        metricTitle: "In Progress",
        statValue: (startedData?.count ?? 0) + (part1SubmittedData?.count ?? 0) + (part2SubmittedData?.count ?? 0),
        icon: Clock4,
        iconColor: "text-blue-900",
        iconBgColor: "bg-blue-100",
        isLoading: isLoadingStarted || isLoadingPart1Submitted || isLoadingPart2Submitted,
      },
      {
        metricTitle: "Awaiting Review",
        statValue: (part1ApprovedData?.count ?? 0) + (furtherInfoPart1Data?.count ?? 0) + (furtherInfoPart2Data?.count ?? 0),
        icon: Info,
        iconColor: "text-yellow-900",
        iconBgColor: "bg-yellow-100",
        isLoading: isLoadingPart1Approved || isLoadingFurtherInfoPart1 || isLoadingFurtherInfoPart2,
      },
      {
        metricTitle: "Rejected",
        statValue: rejectedData?.count ?? 0,
        icon: TriangleAlert,
        iconColor: "text-red-900",
        iconBgColor: "bg-red-100",
        isLoading: isLoadingRejected,
      },
    ];

    return metrics;
  }, [
    startedData?.count,
    part1SubmittedData?.count,
    part1ApprovedData?.count,
    part2SubmittedData?.count,
    rejectedData?.count,
    isLoadingStarted,
    isLoadingPart1Submitted,
    isLoadingPart1Approved,
    isLoadingPart2Submitted,
    isLoadingRejected,
    approvedData?.count,
    furtherInfoPart1Data?.count,
    furtherInfoPart2Data?.count,
    isLoadingFurtherInfoPart1,
    isLoadingFurtherInfoPart2,
    isLoadingApproved,
    totalData?.count,
    isLoadingTotal,
  ]);

  return metrics;
};
